import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { 
  Building2, 
  FileText, 
  Newspaper, 
  ArrowRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  Users,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

interface DashboardStats {
  affiliations: number;
  pendingAffiliations: number;
  proxyRelationships: number;
  submissions: number;
  pendingSubmissions: number;
  publishedContent: number;
}

export default function UserDashboard() {
  const { user } = useAuth();

  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/dashboard/user-stats"],
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold" data-testid="text-dashboard-title">
          Welcome back, {user?.firstName || "User"}
        </h1>
        <p className="text-muted-foreground">
          Manage your content, affiliations, and client relationships
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card data-testid="card-affiliations-stat">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">My Affiliations</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.affiliations || 0}</div>
            {(stats?.pendingAffiliations || 0) > 0 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <Clock className="h-3 w-3" />
                {stats?.pendingAffiliations} pending approval
              </p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="card-clients-stat">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Client Companies</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.proxyRelationships || 0}</div>
            <p className="text-xs text-muted-foreground">Active proxy relationships</p>
          </CardContent>
        </Card>

        <Card data-testid="card-submissions-stat">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Content Submissions</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.submissions || 0}</div>
            {(stats?.pendingSubmissions || 0) > 0 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <AlertCircle className="h-3 w-3" />
                {stats?.pendingSubmissions} pending review
              </p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="card-published-stat">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.publishedContent || 0}</div>
            <p className="text-xs text-muted-foreground">Articles live on site</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Newspaper className="h-5 w-5" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Start creating content or manage your relationships
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link href="/dashboard/press-releases">
              <Button className="w-full justify-between" variant="outline" data-testid="button-create-press-release">
                Create Press Release
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link href="/dashboard/affiliations">
              <Button className="w-full justify-between" variant="outline" data-testid="button-manage-affiliations">
                Manage Affiliations
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link href="/dashboard/clients">
              <Button className="w-full justify-between" variant="outline" data-testid="button-view-clients">
                View Client Companies
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Recent Submissions
            </CardTitle>
            <CardDescription>
              Your latest content submissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No recent submissions</p>
              <Link href="/dashboard/press-releases">
                <Button variant="ghost" size="sm" className="mt-2">
                  Create your first press release
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
